import { Paragraph as RevisionParagraph } from "~/types/Paragraph";
import { useCalculateNumParagraphColumns } from "../wordGenerator/useCalculateNumParagraphColumns";
import { useClipboardChecklistSectionGenerator } from "./useClipboardChecklistSectionGenerator";

import { DocumentSubmissionType } from "~/types/enums/DocumentSubmissionType.enum";

export const useClipboardChecklistGenerator = (
  submittedDocument: SubmittedDocument,
  checklist: Checklist | undefined,
  wordChecklistCells: WordChecklistCells | undefined,
  allCriteria: ChecklistCriteria[] | undefined,
  totalEarnedScore: number,
  totalScore: number,
  commentColor: number[]
) => {
  if (!checklist) return;

  const numParagraphs = checklist.numParagraphs;
  const numParagraphColumns =
    useCalculateNumParagraphColumns(checklist.sections ?? []) ?? numParagraphs;

  const overrideParagraphText: RevisionParagraph[] = [];
  const cellTextToParagraphs: CellTextParagraphs[] = [];

  for (const cell of wordChecklistCells ?? []) {
    const cellText = [];

    for (const paragraph of cell.paragraphs) {
      cellText.push(paragraph.text);
      overrideParagraphText.push(paragraph);
    }

    const cellTextString = cellText.join(" ");
    cellTextToParagraphs.push({
      text: cellTextString,
      paragraphs: cell.paragraphs,
    });
  }

  const container = document.createElement("div");

  // Add your row generation logic for each cell as needed here...
  // for (const criteriaInstance of checklist.criteriaInstances) {

  //     const criteriaRows = useClipboardChecklistCriteriaGenerator(criteriaInstance, allCriteria ?? [], numParagraphs, numParagraphColumns, cellTextToParagraphs);

  //     for (const criteriaRow of criteriaRows) {
  //         tableElement.appendChild(criteriaRow);
  //     }
  // }

  let lastSectionTableElement: HTMLTableElement | undefined = undefined;
  let lastSectionNumberParagraphs: number | undefined = undefined;

  if (checklist.sections) {
    var sectionIndex = 0;

    for (const section of checklist.sections) {
      const maxNumParagraphsInSection =
        useGetMaxNumParagraphsInSection(section);

  
      const sectionRows: HTMLTableRowElement[] = [];

      const newTableElement = document.createElement("table");
      newTableElement.style.border = "1px solid black";
      newTableElement.style.borderCollapse = "collapse";
      // newTableElement.style.tableLayout = 'fixed';
      newTableElement.style.width = "100%"; // Take up full width

      if (sectionIndex == 0) {
        const headerRow = document.createElement("tr");
        const header = [
          {
            text: "X",
            bold: true,
            width: "30px", // Set a fixed width for the "X" column
          },
          {
            text: "Task",
            bold: true,
            width: "auto", // Let this column take up the most space
          },
          {
            text: "Score",
            bold: true,
            alignment: "center",
            width: "150px", // Set a fixed width for the "Score" column
            colSpan: (maxNumParagraphsInSection ?? 1).toString(),
          },
        ];

        for (const cell of header) {
          const headerCell = document.createElement("th");
          headerCell.textContent = cell.text;
          headerCell.style.border = "1px solid black";
          headerCell.style.padding = "8px";
          headerCell.style.textAlign = cell.alignment ?? "left";
          headerCell.style.width = cell.width; // Apply the specified width for each column
          headerCell.setAttribute("colspan", cell.colSpan ?? "1");
          headerRow.appendChild(headerCell);
        }
        newTableElement.appendChild(headerRow);
      }

      useClipboardChecklistSectionGenerator(
        section,
        sectionRows,
        allCriteria ?? [],
        false,
        numParagraphs,
        maxNumParagraphsInSection,
        cellTextToParagraphs
      );

      for (const sectionRow of sectionRows) {
        newTableElement.appendChild(sectionRow);
      }

      container.appendChild(newTableElement);

      sectionIndex++;

      lastSectionTableElement = newTableElement;
      lastSectionNumberParagraphs = maxNumParagraphsInSection;
    }
  }

  if (lastSectionTableElement) {
    for (const customCriteria of checklist.customCriteriaInstances ?? []) {
      const row = document.createElement("tr");

      const cell1 = document.createElement("td");
      cell1.textContent = "";
      cell1.style.border = "1px solid black";
      cell1.style.padding = "8px";
      cell1.style.textAlign = "left";
      row.appendChild(cell1);

      const cell2 = document.createElement("td");
      cell2.textContent = customCriteria.labelOverride ?? "";
      cell2.style.border = "1px solid black";
      cell2.style.padding = "8px";
      cell2.style.textAlign = "right";
      cell2.style.color = "red";
      row.appendChild(cell2);

      const cell3 = document.createElement("td");
      cell3.textContent = (customCriteria.earnedPoints ?? 0).toString();
      cell3.style.border = "1px solid black";
      cell3.style.padding = "8px";
      cell3.style.textAlign = "center";
      cell2.style.color = "red";
      cell3.setAttribute(
        "colspan",
        (lastSectionNumberParagraphs ?? 1).toString()
      );
      row.appendChild(cell3);

      lastSectionTableElement.appendChild(row);
    }

    const showTotal =
      (submittedDocument.documentSubmissionType == undefined ||
        submittedDocument.documentSubmissionType ==
          DocumentSubmissionType.finalDraft) &&
      submittedDocument.allowFinalDraftGradedState != true;

    if (showTotal) {
      const footerRow = document.createElement("tr");

      const cell1 = document.createElement("td");
      cell1.textContent = "";
      cell1.style.border = "1px solid black";
      cell1.style.padding = "8px";
      cell1.style.textAlign = "left";
      footerRow.appendChild(cell1);

      const cell2 = document.createElement("td");
      cell2.textContent = "Total";
      cell2.style.border = "1px solid black";
      cell2.style.padding = "8px";
      cell2.style.textAlign = "right";
      footerRow.appendChild(cell2);

      const cell3 = document.createElement("td");
      cell3.textContent = `${totalEarnedScore.toString()}/${totalScore}`;
      cell3.style.border = "1px solid black";
      cell3.style.textAlign = "center";
      cell3.setAttribute(
        "colspan",
        (lastSectionNumberParagraphs ?? 1).toString()
      );
      footerRow.appendChild(cell3);

      lastSectionTableElement.appendChild(footerRow);
    }

    if (
      showTotal != true &&
      submittedDocument.documentGradedState != undefined
    ) {
      const footerRow = document.createElement("tr");

      const cell1 = document.createElement("td");
      cell1.textContent =
        DocumentGradedStateLabels[
          submittedDocument.documentGradedState ??
            DocumentGradedState.incomplete
        ];
      cell1.style.border = "1px solid black";
      cell1.style.padding = "8px";
      cell1.style.textAlign = "center";
      cell1.style.color =
        submittedDocument.documentGradedState == DocumentGradedState.incomplete
          ? "#B2985C"
          : "#5A6E4F";
      cell1.setAttribute(
        "colspan",
        ((lastSectionNumberParagraphs ?? 1) + 2).toString()
      );
      footerRow.appendChild(cell1);

      lastSectionTableElement.appendChild(footerRow);
    }
  }

  return container;
};
